<template>
  <v-card class="fcu-widget" data-testid="free-cars-upcoming-widget">
    <v-card-title class="pb-2 fcu-widget__title">{{ $t("widgets.free_cars_upcoming") }}</v-card-title>
    <!-- высоту ячейки сетки подгоняет главная по измеренной высоте содержимого
         (событие content-height); строк больше MAX_VISIBLE_ROWS — прокрутка внутри,
         заголовок остаётся на месте -->
    <v-card-text class="px-2 pb-2 fcu-widget__body">
      <div ref="content">
      <div v-if="!data" class="text-caption text--secondary px-2">{{ $t("widgets.free_cars_upcoming_empty") }}</div>
      <template v-else>
        <!-- FT-217 REQ-08: строки — ценовые группы, «Без цены» и «Всего»; колонки — три дня;
             ячейка «N из M» ведёт на доску «Ближайшие дни» с фильтром группы к колонке дня -->
        <v-simple-table dense class="fcu-widget__table">
          <thead>
            <tr>
              <th class="px-1"></th>
              <th v-for="(day, index) in days" :key="index" class="text-center px-1 fcu-widget__day">
                <div>{{ dayName(index) }}</div>
                <div class="text-caption text--secondary">{{ dayDate(day) }}</div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.key" :class="row.total ? 'font-weight-medium' : ''"
              :data-testid="row.total ? 'fcu-total-row' : 'fcu-group-row'">
              <td class="px-1 fcu-widget__label">{{ row.label }}</td>
              <td v-for="(cell, index) in row.cells" :key="index" class="text-center px-1">
                <a href="#" class="fcu-widget__cell text-decoration-none" data-testid="fcu-cell"
                  @click.prevent="openBoard(row, index)">
                  <span class="text-no-wrap">{{ $t("cars_board.n_of_m", { free: cell.free, total: cell.total }) }}</span>
                  <!-- REQ-07: разбивка только если сервер её прислал (superadmin, admin
                       при наличии партнёрских машин) -->
                  <!-- На узком экране — иконки с числом в одну строку: текст «своих · партн.»
                       в ячейке ~90 px раздвигал таблицу, и колонка подписей схлопывалась в
                       столбик по букве (стенд 23.10, 375 px). Текст — в title и aria-label. -->
                  <div v-if="hasSplit(cell) && compact" class="text-caption text--secondary text-no-wrap"
                    data-testid="fcu-split" :title="splitText(cell)" :aria-label="splitText(cell)">
                    <v-icon x-small>mdi-home-outline</v-icon>{{ cell.free_own }}
                    <v-icon x-small class="ml-1">mdi-handshake-outline</v-icon>{{ cell.free_partner }}
                  </div>
                  <div v-else-if="hasSplit(cell)" class="text-caption text--secondary text-no-wrap" data-testid="fcu-split">
                    {{ splitText(cell) }}
                  </div>
                </a>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
        <div v-if="data.has_groups === false" class="d-flex align-center flex-wrap px-1 pt-2" style="gap: 8px"
          data-testid="fcu-no-groups">
          <span class="text-caption text--secondary">{{ $t("price_groups.not_built") }}</span>
          <v-btn v-if="isAdminPlus" x-small color="primary" class="ml-auto" :loading="building"
            data-testid="fcu-build-groups" @click="buildGroups">
            {{ $t("price_groups.build") }}
          </v-btn>
        </div>
      </template>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { NO_GROUP_KEY, priceGroupLabel, priceGroupLabelOptions } from "@/utils/priceGroupLabel";
import { upcomingDateText, upcomingDayName } from "@/utils/upcomingDays";
import { rebuildPriceGroups } from "@/utils/priceGroupsApi";
import { MAX_VISIBLE_ROWS } from "@/utils/freeCarsUpcomingLayout";

export default {
  name: "FreeCarsUpcomingWidget",
  props: {
    // free_cars_upcoming из dashboard_data (CTR-04); null — данных ещё нет
    data: { type: Object, default: null },
    // узкий экран (главная: isMobile — xs/sm, или нативное приложение)
    compact: { type: Boolean, default: false },
  },
  data() {
    return { building: false };
  },
  watch: {
    data() { this.scheduleMeasure(); },
    compact() { this.scheduleMeasure(); },
  },
  mounted() {
    // Высота содержимого меняется не только с данными: ширина ячейки (поворот,
    // ресайз окна, раскладка сетки), шрифты. ResizeObserver на обёртке содержимого;
    // без него — resize окна.
    if (typeof ResizeObserver !== "undefined" && this.$refs.content) {
      this.resizeObserver = new ResizeObserver(() => this.scheduleMeasure());
      this.resizeObserver.observe(this.$refs.content);
    } else if (typeof window !== "undefined") {
      this.onWindowResize = () => this.scheduleMeasure();
      window.addEventListener("resize", this.onWindowResize);
    }
    this.scheduleMeasure();
  },
  beforeDestroy() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.onWindowResize) window.removeEventListener("resize", this.onWindowResize);
  },
  computed: {
    days() {
      return (this.data && this.data.days) || [];
    },
    rows() {
      if (!this.data) return [];
      const opts = priceGroupLabelOptions(this, this.data.unit);
      // Без групп — только «Всего» (REQ-08); строки групп сервер в этом случае не шлёт,
      // но и «Без цены» без групп ничего не добавляет к итогу.
      const groups = this.data.has_groups === false ? [] : this.data.rows || [];
      const rows = groups.map((row) => ({
        key: String(row.group_id),
        groupId: row.group_id,
        label: priceGroupLabel(row.group_id === NO_GROUP_KEY ? null : row, opts),
        cells: row.cells || [],
      }));
      const total = this.data.total_row;
      if (total) {
        rows.push({ key: "total", total: true, groupId: null, label: this.$t("widgets.free_cars_total"), cells: total.cells || [] });
      }
      return rows;
    },
  },
  methods: {
    dayName(index) {
      return upcomingDayName(index, this.$t.bind(this));
    },
    dayDate(day) {
      return upcomingDateText(day, this.$i18n && this.$i18n.locale);
    },
    splitText(cell) {
      return this.$t("widgets.free_cars_split", { own: cell.free_own, partner: cell.free_partner });
    },
    scheduleMeasure() {
      this.$nextTick(() => this.measure());
    },
    // FT-217 SC-08: сколько пикселей нужно карточке, чтобы показать все строки без
    // прокрутки: заголовок + содержимое тела + его отступы. Меряется обёртка
    // содержимого, а не scrollHeight тела — тот не меньше высоты самой ячейки, и
    // после роста виджет уже не смог бы уменьшиться. Больше MAX_VISIBLE_ROWS строк —
    // до низа десятой строки, дальше прокрутка. Главная переводит пиксели в ряды сетки.
    measure() {
      // v-card-title / v-card-text — функциональные компоненты, ref на них не ставится
      const title = this.$el && this.$el.querySelector(".fcu-widget__title");
      const body = this.$el && this.$el.querySelector(".fcu-widget__body");
      const content = this.$refs.content;
      if (!title || !body || !content || !this.data) return;
      const style = window.getComputedStyle(body);
      const pad = (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
      let height = content.offsetHeight;
      const rows = content.querySelectorAll("tbody tr");
      if (rows.length > MAX_VISIBLE_ROWS) {
        const last = rows[MAX_VISIBLE_ROWS - 1].getBoundingClientRect();
        height = last.bottom - content.getBoundingClientRect().top;
      }
      if (!height) return; // не в DOM / скрыт — мерить нечего
      const px = Math.ceil(title.offsetHeight + height + pad);
      if (px === this.lastHeight) return;
      this.lastHeight = px;
      this.$emit("content-height", px);
    },
    hasSplit(cell) {
      return cell.free_own != null && cell.free_partner != null;
    },
    // Доска — /vehicles/board (имя маршрута CarsBoard); фильтр и день — в query, их
    // читает carsQueryState. «Всего» — без фильтра группы.
    openBoard(row, dayIndex) {
      const query = { group_by: "upcoming", day: String(dayIndex) };
      if (row.groupId != null) query.price_group_id = String(row.groupId);
      this.$router.push({ name: "CarsBoard", query }).catch(() => {});
    },
    // REQ-03: «Собрать группы» — та же пересборка, что в настройках (superadmin, admin)
    async buildGroups() {
      if (this.building) return;
      this.building = true;
      try {
        await rebuildPriceGroups(this, { dryRun: false });
        this.$notify && this.$notify(this.$t("price_groups.built"), "", "success");
        this.$emit("rebuilt");
      } catch (error) {
        this.setError && this.setError(error, this.$t("price_groups.build_error"));
      } finally {
        this.building = false;
      }
    },
  },
};
</script>

<style scoped>
.fcu-widget {
  height: 100%;
  display: flex;
  flex-direction: column;
}
.fcu-widget__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}
/* узкий экран 375 px: четыре колонки помещаются без горизонтальной прокрутки */
.fcu-widget__table >>> td,
.fcu-widget__table >>> th {
  height: auto !important;
  padding-top: 4px !important;
  padding-bottom: 4px !important;
}
.fcu-widget__label {
  min-width: 0;
  word-break: break-word;
}
.fcu-widget__day {
  white-space: nowrap;
}
.fcu-widget__cell {
  display: inline-block;
}
</style>
